import { Dropdown, Menu } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  CheckboxInput,
  SelectInput,
  BlackUndoIcon as blackUndoIcon,
  EcgToolbarArrowIconBlack as ToolbarArrowIcon,
} from '@/shared/ui';
import { sizeThumbnailMenu } from '@/entities/ecg';
import { VALID_DEVICE_EVENT_STATUS_OPTIONS } from '@/widgets/strips-management/ui/constants';
import DevicePaginationContainer from './devicePaginationContainer';
import useStripsDeviceActionButton from './useStripsDeviceActionButton';

export type StripsDeviceActionButtonProps = {
  keyRecoil: any;
  activeButton: any;
  studyData?: any;
};

/**
 * Renders the device events toolbar with the validity select, include-in-report and include-strip checkboxes, size dropdown and pagination. Reads config flags and handlers from useStripsDeviceActionButton, disables the editing controls while strips load or when the event falls in a no-data zone and explains that in a tooltip, and reveals the reset button only once foundEventChange is set.
 *
 * @param props - The study data passed through to the device pagination container.
 * @returns The device events toolbar row with its controls and pagination.
 */
const StripsDeviceActionButton = ({
  keyRecoil: keyRecoilProp,
  activeButton,
  studyData,
}: StripsDeviceActionButtonProps) => {
  const {
    keyRecoil,
    isLoadingStrip,
    size,
    forceCloseMenu,
    configInfo,
    onClickItemSize,
    onChangeCheckbox,
    onChangeSelect,
    onClickReset,
  } = useStripsDeviceActionButton({ keyRecoil: keyRecoilProp, activeButton });

  /**
   * Renders the thumbnail-size dropdown of the device events toolbar. Maps sizeThumbnailMenu into menu items, marks the entry matching the current size as selected, and routes selection to onClickItemSize.
   *
   * @returns The size dropdown with its trigger button showing the current size.
   */
  const renderSizeDropdown = () => {
    const items: any[] = [];
    _.forEach(sizeThumbnailMenu, (x, i) =>
      items.push({
        key: `size-${i}`,
        label: <div className={classNames(x === size ? 'selected' : '')}>{x}</div>,
        value: x,
      }),
    );
    return (
      <Dropdown
        trigger={['click']}
        overlay={<Menu onClick={onClickItemSize} className="thumbnail-size-menu" items={items} />}
      >
        <button id="device-event-thumbnail-size-btn" className="thumbnail-size-text">
          {`Size: ${size}`}
          <img src={ToolbarArrowIcon} alt="arrow-icon" />
        </button>
      </Dropdown>
    );
  };

  return (
    <div
      id="button-types"
      className="strip-management-content-top-container --min-width-strip-event"
    >
      <div className="toolbar">
        <SelectInput
          className="valid-select-input"
          name="isValid"
          ariaLabel="Validity"
          options={VALID_DEVICE_EVENT_STATUS_OPTIONS as any} // pre-existing: option.value is boolean, SelectInputOption.value is string|number
          placeholder="Invalid"
          value={configInfo.isValid}
          onChange={onChangeSelect}
          isUnsetMinWidth
          disabled={
            configInfo.isDisabled ||
            isLoadingStrip ||
            configInfo.isNoEcgData ||
            configInfo.carriedBySentReport
          }
          forceCloseMenu={forceCloseMenu}
          couldForceCloseMenu
          hasNoBoxShadow
          containerId="valid-device-event-select"
        />
        {(configInfo.isNoEcgData || configInfo.carriedBySentReport) && (
          <UncontrolledTooltip
            className="custom-tooltip"
            target="valid-device-event-select"
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            {configInfo.carriedBySentReport
              ? 'This event is carried by a sent report. Recall the report to change its validity.'
              : 'This event has no ECG data. Its validity can not be changed.'}
          </UncontrolledTooltip>
        )}
        <CheckboxInput
          id="isIncludedToReport"
          checked={!configInfo.isDisabled && configInfo.isIncludedToReport}
          name="isIncludedToReport"
          onChange={(name, value) => {
            onChangeCheckbox(name, value);
          }}
          data={
            {
              label: 'Include event in report',
              value: !configInfo.isIncludedToReport, // pre-existing bug: CheckboxInput's `data.value` is typed string|number
            } as any
          }
          disabled={configInfo.isDisabled || isLoadingStrip}
          className="included-row-checkbox width-186"
          isIndeterminate={!configInfo.isDisabled && configInfo.isIndeterminateToReport}
        />
        <div id="included-strip-checkbox-id">
          <CheckboxInput
            id="isIncludedStrip"
            checked={
              !(configInfo.isDisabledStrip || configInfo.isDisabled) && configInfo.isIncludedStrip
            }
            name="isIncludedStrip"
            onChange={(name, value) => {
              onChangeCheckbox(name, value);
            }}
            data={
              {
                label: 'Include strip',
                value: !configInfo.isIncludedStrip, // pre-existing bug: CheckboxInput's `data.value` is typed string|number
              } as any
            }
            disabled={
              configInfo.isDisabledStrip ||
              configInfo.isDisabled ||
              isLoadingStrip ||
              configInfo.isNoEcgData
            }
            className="included-row-checkbox width-125"
            isIndeterminate={!configInfo.isDisabled && configInfo.isIndeterminateStrip}
          />
        </div>
        {configInfo.isNoEcgData && (
          <UncontrolledTooltip
            className="custom-tooltip"
            target="included-strip-checkbox-id"
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            Cannot edit events with unavailable strips
          </UncontrolledTooltip>
        )}
        {!!configInfo.foundEventChange && (
          <>
            <div className="btn-type-vertical-line" />
            <IconButton
              id="btnResetDeviceEvents"
              isOutline
              iconComponent={<img src={blackUndoIcon} alt="Noise icon" />}
              onClick={onClickReset}
              className="reset-btn"
            />
            <UncontrolledTooltip
              className="custom-tooltip"
              target="btnResetDeviceEvents"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
            >
              <div className="beat-tooltip">Reset changes</div>
            </UncontrolledTooltip>
          </>
        )}
      </div>
      {renderSizeDropdown()}
      <div />
      <div className="toolbar-container">
        <DevicePaginationContainer
          studyData={studyData}
          keyRecoil={keyRecoil}
          isDisabled={isLoadingStrip}
        />
      </div>
    </div>
  );
};

export default StripsDeviceActionButton;
